/**
 * German labels for backend enum values (keys mirror the backend). Unknown keys fall
 * back to the raw value via `labelOf` – never hide data just because a label is missing.
 */

/** @param {Record<string, string>} map @param {string|null|undefined} key @param {string} [fallback] */
export function labelOf(map, key, fallback = '–') {
  if (key === null || key === undefined || key === '') return fallback;
  return map[key] ?? key;
}

export const PARTY_TYPE_LABELS = { person: 'Person', organization: 'Organisation' };

/** parties.source – where the record is maintained. Only `manual` is editable in BLIMO. */
export const PARTY_SOURCE_LABELS = {
  manual: 'Manuell',
  cobra: 'Cobra',
  wincasa: 'WinCasa',
  se: 'SE-Portal',
  oldportal: 'Altportal',
};

/** Party roles for the list filter (`filter[role]`). */
export const PARTY_ROLE_FILTER_LABELS = { owner: 'Eigentümer', tenant: 'Mieter', client: 'Mandant' };

export const CONTACT_KIND_LABELS = { email: 'E-Mail', phone: 'Telefon', mobile: 'Mobil', fax: 'Fax', website: 'Website' };

export const ADDRESS_KIND_LABELS = { postal: 'Postanschrift', delivery: 'Lieferanschrift', invoice: 'Rechnungsanschrift' };

export const CONSENT_LABELS = { share_contact_with_co_owners: 'Kontaktdaten für Miteigentümer sichtbar' };

/** unit_types.key (UnitTypeSeeder) */
export const UNIT_TYPE_LABELS = {
  apartment: 'Wohnung',
  house: 'Haus',
  commercial: 'Gewerbe',
  parking_space: 'Stellplatz',
  underground_garage: 'Tiefgarage',
  garage: 'Garage',
  plot: 'Parzelle',
  leasehold: 'Erbpacht',
  other: 'Sonstiges',
};

/** property_roles.role + portal `my_roles` (owner = Eigentümer einer Einheit, client = Mandant/Investor) */
export const PROPERTY_ROLE_LABELS = { owner: 'Eigentümer', client: 'Mandant', auditor: 'Kassenprüfer', meter_reader: 'Ableser', caretaker: 'Hausmeister' };

/** ownership_parties.role */
export const OWNERSHIP_PARTY_ROLE_LABELS = { primary: 'Eigentümer', co: 'Miteigentümer' };

/** tenancy_parties.role */
export const TENANCY_PARTY_ROLE_LABELS = { main: 'Hauptmieter', co: 'Mitmieter' };

export const LOCALE_LABELS = { de: 'Deutsch', en: 'Englisch' };

/** Roles that only users with `roles.manage` may grant or revoke (UserPolicy::PRIVILEGED_ROLES). */
export const PRIVILEGED_ROLES = ['super-admin', 'admin'];

/** External reference systems (external_references.system). */
export const EXTERNAL_SYSTEM_LABELS = { cobra: 'Cobra', wincasa: 'WinCasa', se: 'SE-Portal', oldportal: 'Altportal', manual: 'Manuell' };

/** open_items.op_type (Filter `op_type`) */
export const OP_TYPE_LABELS = { MIETE: 'Miete', BK: 'Betriebskosten', NEBENKOSTEN: 'Nebenkosten' };

/** import_runs.source */
export const IMPORT_SOURCE_LABELS = { accounting: 'Buchhaltung (WinCasa)', cobra: 'Cobra', mail: 'Postfach (IMAP)', csv: 'Datei-Import (CSV/XLSX)' };

/** import_runs.kind */
export const IMPORT_KIND_LABELS = {
  sync: 'Mietdaten & OP',
  costs: 'Kosten',
  accounts: 'Sachkonten',
  owners: 'Eigentümer',
  payments: 'Zahlungseingänge',
  heating: 'Heizkosten-Anhänge',
  parties: 'Personen',
};

/** Counters in import_runs.stats (keys from AccountingSyncImporter); unknown keys are shown raw. */
export const IMPORT_STAT_LABELS = {
  warnings: 'Warnungen',
  payments: 'Zahlungen',
  payments_without_tenancy: 'davon ohne Mietverhältnis',
  messages: 'Nachrichten',
  attachments: 'Anhänge',
  stored: 'abgelegt',
  duplicates: 'Dubletten',
  skipped: 'abgelehnt',
  property_matched: 'Liegenschaft erkannt',
  date_folder: 'Datumsordner',
  marked_processed: 'als verarbeitet markiert',
  rows: 'Zeilen',
  created: 'angelegt',
  updated: 'aktualisiert',
  failed: 'fehlgeschlagen',
};

/** Filter of GET /admin/announcements (`filter[status]`), decided by the API. */
export const ANNOUNCEMENT_STATUS_FILTER_LABELS = { live: 'Live', draft: 'Entwurf', scheduled: 'Geplant', expired: 'Abgelaufen' };

/** outbound_messages.source */
export const OUTBOUND_SOURCE_LABELS = { platform: 'Plattform', oldportal: 'Altportal' };

/** messages.direction: inbound = from the owner, outbound = reply of the management. */
export const MESSAGE_DIRECTION_LABELS = { inbound: 'Eigentümer', outbound: 'Verwaltung' };

/** Filter `status` of GET /admin/meter-readings (MeterReading::OPEN|READ|CLOSED). */
export const METER_READING_STATUS_FILTER_LABELS = { open: 'Offen', read: 'Abgelesen', closed: 'Ohne Ablesung' };

/** Erfassungsart je Ableser (`manual` der Ablesungen): App = Ableser erfasst im Portal, manuell = Verwaltung trägt ein. */
export const METER_READER_MODE_LABELS = { app: 'App', manual: 'Manuell' };

/** Serienmail an Ableser (POST …/reader-mails `kind`). */
export const METER_READER_MAIL_KIND_LABELS = { first: 'Erstanschreiben', reminder: 'Erinnerung' };

/** Exporte (GET /admin/meter-readings/export `kind`). */
export const METER_EXPORT_KIND_LABELS = { readings: 'Zählerstände (XLSX)', changes: 'Zählerwechsel (XLSX)' };

/** meters.source */
export const METER_SOURCE_LABELS = { manual: 'Manuell', cobra: 'Cobra', oldportal: 'Altportal' };

/** proxy_authorizations.attorney_type (ProxyAuthorization::ATTORNEY_TYPES) */
export const ATTORNEY_TYPE_LABELS = { name: 'Eine Person (Name)', administrator: 'Verwalter', co_owner: 'Miteigentümer' };

/** Amount columns of letting tickets (LettingTicket::toApi / portal `totals`). */
export const LETTING_AMOUNT_LABELS = { rent: 'Miete', garage_rent: 'Stellplatz', operating_costs: 'Betriebskosten', heating_costs: 'Heizkosten' };

/** Object roles that the administration can assign (POST /admin/properties/{id}/roles). */
export const ASSIGNABLE_PROPERTY_ROLES = ['auditor', 'meter_reader'];

/** filter[status] of the tenancy lists (admin and portal; API default `current`). */
export const TENANCY_STATUS_FILTER_LABELS = { current: 'Aktuell', past: 'Ehemalig', all: 'Alle' };

/** Result groups of the global search (GET /admin/search), in display order. */
export const SEARCH_GROUP_LABELS = {
  properties: 'Liegenschaften',
  units: 'Einheiten',
  parties: 'Personen & Organisationen',
  documents: 'Dokumente',
  announcements: 'Mitteilungen',
};

/** contacts[].role of GET /portal/home */
export const HOME_CONTACT_ROLE_LABELS = { manager: 'Verwalter', contact: 'Ansprechpartner' };

/** Zielgruppe eines Links (F-1005) */
export const LINK_AUDIENCE_LABELS = { all: 'Alle Portalbenutzer', roles: 'Bestimmte Rollen', users: 'Einzelne Benutzer' };

/** Benutzerliste: Filter befristeter Zugänge (F-005, filter[access]) */
export const USER_ACCESS_FILTER_LABELS = { expiring: 'Läuft in 7 Tagen ab', expired: 'Abgelaufen', limited: 'Befristet', unlimited: 'Unbefristet' };

/** Herkunft der Koordinaten einer Liegenschaft (F-115) */
export const COORDINATES_SOURCE_LABELS = { manual: 'manuell', nominatim: 'OpenStreetMap (Nominatim)', google: 'Google', oldportal: 'Altportal' };
